import type { MaybeRefOrGetter, Ref } from 'vue';
import { onScopeDispose, readonly, ref, toValue, watch } from 'vue';

/**
 * Returns a readonly ref that mirrors the source boolean, but delays transitions from `false` to `true`
 * by the specified duration. Transitions from `true` to `false` are immediate.
 */
export function useDeferredTrue(source: MaybeRefOrGetter<boolean>, delayMs: number): Readonly<Ref<boolean>> {
	const delayed = ref(false);
	let timeout: ReturnType<typeof setTimeout> | undefined;

	watch(
		() => toValue(source),
		(value) => {
			if (timeout) {
				clearTimeout(timeout);
				timeout = undefined;
			}

			if (value) {
				timeout = setTimeout(() => {
					delayed.value = true;
					timeout = undefined;
				}, delayMs);
			}
			else {
				delayed.value = false;
			}
		},
		{ immediate: true, flush: 'sync' },
	);

	onScopeDispose(() => {
		if (timeout) {
			clearTimeout(timeout);
			timeout = undefined;
		}
	});

	return readonly(delayed);
}
